// Die Dachplanung mit K2 herunterladen (Gernot beim Augentest E3,
// 21.10.2026: „Öffnen“ lud bei ihm eine Datei herunter, deren Name die
// Prüfsumme war – „herunterladen tue ich es, wenn ich es herunterladen
// möchte“). „Ansehen“ (bis 23.10.2026 „Öffnen“) zeigt das PDF im Browser; „Herunterladen“ speichert es
// mit einem Namen, den ein Mensch lesen kann: „Dachplanung K2 – Familie
// Maier.pdf“.
//
// Der Kundenname steht nur im Namen der Datei (dem `download` des Links),
// nie in der Adresse – Adressen landen in Protokollen und im Verlauf.
//
// Seit E3b (O76, Gernots Regel vom 22.10.2026): Jede Datei, die man aus dem
// System speichert, beginnt mit dem Datum – dem Tag der Besichtigung –, und
// am Ende steht die Produktlinie.
//
// Seit dem Augentest E3b (Gernot, 23.10.2026): Die Teile stehen mit
// Unterstrich getrennt, in jedem Teil bleiben die Leerzeichen –
// „JJJJ-MM-TT_<Art>_<Kunde>_<Produkt>“, für jede Datei, die das CRM benennt
// (`dateiname`): „2026-10-22_Dachplanung K2_Familie Maier_Happy Energy
// Sun.pdf“, „2026-10-22_Unterlage_Familie Maier_Happy Energy Sun“,
// „2026-10-23_Materialliste_Familie Maier_Happy Energy Sun“. Fehlt ein Teil,
// fällt er weg – nie zwei Unterstriche hintereinander.

/** So viele Zeichen höchstens vom Kundennamen – der ganze Name bleibt weit unter dem, was Windows erlaubt. */
export const NAME_HOECHSTENS = 120;

/** Was Windows in Dateinamen nicht mag: \ / : * ? " < > | und Steuerzeichen. */
const VERBOTEN = /[\\/:*?"<>|\p{Cc}]/gu;

/** Ein Name, wie er in einen Dateinamen darf: ohne Zeichen, die Windows nicht mag, gekürzt, am Ende kein Punkt und kein Leerzeichen. */
function fuerDateinamen(text: string): string {
  const sauber = text.replace(VERBOTEN, " ").replace(/\s+/g, " ").trim();
  return [...sauber].slice(0, NAME_HOECHSTENS).join("").replace(/[. ]+$/, "");
}

/** Was vorne und hinten an einen Dateinamen kommt (seit E3b): der Tag („JJJJ-MM-TT“) und die Produktlinie. */
export interface DateiRahmen {
  /** „JJJJ-MM-TT“ – null: unbekannt, dann ohne Datum. */
  tag: string | null;
  /** „Happy Energy Sun“ – null: unbekannt (etwa ein Server mit altem Stand), dann ohne. */
  produkt: string | null;
}

/**
 * Setzt einen Dateinamen aus seinen Teilen zusammen – die eine Stelle für
 * Gernots Regel (23.10.2026): Teile mit Unterstrich getrennt, jeder Teil
 * ohne Zeichen, die Windows nicht mag; leere Teile fallen weg.
 */
export function dateiname(teile: readonly (string | null | undefined)[]): string {
  return teile
    .map((t) => (t ? fuerDateinamen(t) : ""))
    .filter((t) => t !== "")
    .join("_");
}

/**
 * Der Dateiname fürs Herunterladen: „JJJJ-MM-TT_Dachplanung K2_<Kunde>_<Produkt>.pdf“;
 * ohne Rahmen „Dachplanung K2_<Kunde>.pdf“. Zeichen, die Windows nicht mag,
 * werden zu Leerzeichen, mehrere Leerzeichen zu einem; am Ende eines Teils
 * kein Punkt und kein Leerzeichen (auch das mag Windows nicht). Bleibt vom
 * Namen nichts: ohne den Teil („Dachplanung K2.pdf“).
 */
export function dachplanungDateiname(kundeName: string, rahmen?: DateiRahmen): string {
  return `${dateiname([rahmen?.tag, "Dachplanung K2", kundeName, rahmen?.produkt])}.pdf`;
}

/**
 * Der Name der Unterlage, wenn sie als PDF gesichert wird – der Browser nimmt
 * dafür den Titel der Seite: „JJJJ-MM-TT_Unterlage_<Kunde>_<Produkt>“ (O76).
 * Ob Safari am iPad und iPhone ihn übernimmt, prüft Gernot (PROJEKT.md, O76).
 * Vor der Freigabe „JJJJ-MM-TT_Unterlage Entwurf_…“ – damit keiner einen
 * Entwurf mit der fertigen Unterlage verwechselt (Gernot, 23.10.2026).
 */
export function unterlageDateiname(kundeName: string, rahmen: DateiRahmen, entwurf = false): string {
  return dateiname([rahmen.tag, entwurf ? "Unterlage Entwurf" : "Unterlage", kundeName, rahmen.produkt]);
}

